import {
  bulkUserSuccessStatsLine,
  bulkStaticDefaults,
} from '@/config/static/catalog/bulk';
import type {
  BulkUserImportDraft,
  BulkUserImportSuccess,
  BulkUserImportFormStep,
} from '@/types/bulkUserImport';
import { WORKFLOW_QUERY } from '@/utils/constants';
import {
  buildBulkUserImportNavigateUrl as buildUrl,
  createLocalBulkUserImportConversationId as createLocalId,
  isBulkUserImportConversation as isRegistered,
  registerBulkUserImportConversation as register,
} from '@/utils/conversationWorkflow';
import { peekConversationAttachmentMeta, peekConversationAttachmentText } from '@/utils/conversationAttachment';
import { parseBulkUserCsv } from '@/utils/parseBulkUserCsv';
import { registerFromDraft } from '@/services/userRegistry';
import type { UserDraft } from '@/types/userCreation';
import type { GuidedFlowRuntime } from './types';
import { serializeBulkUserCsv } from '@/utils/serializeBulkUserCsv';
import { buildBulkApproveUserMessage } from '@/utils/bulkUserPostImport';

const FORM_STEP_ORDER: BulkUserImportFormStep[] = ['review'];
const LOADING_PHASES = ['thinking', 'working', 'almost-ready'] as const;

function emptyDraft(): BulkUserImportDraft {
  return {
    rows: [],
    csvFileName: null,
    hasCsvAtSend: false,
  };
}

function draftFromCsv(conversationId: string): BulkUserImportDraft {
  const meta = peekConversationAttachmentMeta(conversationId);
  const text = peekConversationAttachmentText(conversationId);
  const hasCsv = Boolean(meta && text);
  const rows = hasCsv && text ? parseBulkUserCsv(text) : [];
  return {
    rows,
    csvFileName: meta?.fileName ?? null,
    hasCsvAtSend: hasCsv,
  };
}

function rowToUserDraft(row: BulkUserImportDraft['rows'][number]): UserDraft {
  return {
    firstName: row.firstName,
    lastName: row.lastName,
    email: row.email,
    phone: '',
    department: row.department,
    title: '',
    employmentNumber: '',
    userGroup: row.userGroup,
    mobileNumber: '',
    address: '',
    credentialType: 'card',
    cardNumber: '',
    cardFormat: '26-bit Wiegand (H10301)',
    facilityCode: '',
    pin: '',
    accessGroups: [],
    schedule: '24/7 Always',
    activationDate: new Date().toISOString().split('T')[0],
    expirationDate: '',
  };
}

export const bulkUserImportRuntime: GuidedFlowRuntime<
  BulkUserImportDraft,
  BulkUserImportFormStep,
  BulkUserImportSuccess
> = {
  id: 'bulk-user-import',
  storageKeyPrefix: 'ux3_bulk_user_import_state',
  workflowQuery: WORKFLOW_QUERY.BULK_USER_IMPORT,
  flowTitle: bulkStaticDefaults.flowTitle,
  progressRailSteps: bulkStaticDefaults.progressRailSteps,
  timing: bulkStaticDefaults.timing,
  loadingPhases: LOADING_PHASES,
  formStepOrder: FORM_STEP_ORDER,
  initialFormStep: 'review',
  explainabilityText: bulkStaticDefaults.explainability,
  formMessageAssistantHint: bulkStaticDefaults.assistantHint,
  idleHint: 'Describe a group of users to add, or attach a CSV file.',
  savingTitle: 'Adding users',
  loadingMessage: 'Preparing bulk user import…',
  start: {
    conversationTitle: 'Bulk user import',
    defaultStarterMessage: 'Add a group of users to this system, as attached in the file.',
    clearStoredState: () => {},
    registerConversation: register,
    createLocalConversationId: createLocalId,
    buildNavigateUrl: buildUrl,
    isConversationRegistered: isRegistered,
  },
  emptyDraft,
  createIdleState: (conversationId, userRole) => ({
    conversationId,
    flowId: 'bulk-user-import',
    workflowStatus: 'draft',
    uiPhase: 'idle',
    statusLabel: '',
    progressRail: bulkStaticDefaults.progressRailSteps.map((s) => ({
      id: s.id,
      label: s.label,
      status: 'pending' as const,
    })),
    formStep: 'review',
    draft: emptyDraft(),
    userMessages: [],
    assistantHint: '',
    showExplainability: false,
    explainabilityText: bulkStaticDefaults.explainability,
    showFormWizard: false,
    showAiBanner: false,
    aiFilledFields: [],
    userRole,
    result: null,
  }),
  hydrateStored: (saved) => {
    if (!LOADING_PHASES.includes(saved.uiPhase as (typeof LOADING_PHASES)[number])) {
      return {
        ...saved,
        userRole: saved.userRole ?? 'operator',
        aiFilledFields: saved.aiFilledFields ?? [],
        showAiBanner: false,
      };
    }

    if (saved.userMessages.length === 0) {
      return bulkUserImportRuntime.createIdleState(saved.conversationId, saved.userRole ?? 'operator');
    }

    const draft =
      saved.draft.rows.length > 0 ? saved.draft : draftFromCsv(saved.conversationId);

    return {
      ...saved,
      uiPhase: 'form',
      formStep: 'review',
      statusLabel: bulkStaticDefaults.flowTitle,
      progressRail: saved.progressRail.map((s) => ({ ...s, status: 'completed' as const })),
      workflowStatus: 'awaiting-approval',
      showFormWizard: false,
      draft,
      userRole: saved.userRole ?? 'operator',
      assistantHint: bulkStaticDefaults.assistantHint,
    };
  },
  resolveDraftForLoading: (conversationId, draft) => {
    const fromCsv = draftFromCsv(conversationId);
    if (!fromCsv.hasCsvAtSend) return draft;
    return {
      ...draft,
      hasCsvAtSend: true,
      csvFileName: fromCsv.csvFileName ?? draft.csvFileName,
    };
  },
  resolveProgressRailSteps: ({ conversationId, draft }) => {
    const fromCsv = draftFromCsv(conversationId);
    const hasCsv = draft.hasCsvAtSend || fromCsv.hasCsvAtSend;
    return hasCsv ? bulkStaticDefaults.progressRailSteps : bulkStaticDefaults.noCsvProgressRailSteps;
  },
  buildFormReveal: ({ base }) => {
    const fromCsv = draftFromCsv(base.conversationId);
    const draft =
      base.draft.rows.length > 0
        ? {
            ...base.draft,
            csvFileName: base.draft.csvFileName ?? fromCsv.csvFileName,
            hasCsvAtSend: base.draft.hasCsvAtSend || fromCsv.hasCsvAtSend,
          }
        : fromCsv;
    const assistantHint = draft.hasCsvAtSend
      ? bulkStaticDefaults.assistantHint
      : bulkStaticDefaults.noCsvAssistantHint;
    return {
      uiPhase: 'form',
      formStep: 'review',
      statusLabel: bulkStaticDefaults.flowTitle,
      workflowStatus: 'awaiting-approval',
      showFormWizard: false,
      showExplainability: false,
      assistantHint,
      introAssistantHint: assistantHint,
      draft,
    };
  },
  patchDraftOnFormMessage: (draft) => draft,
  isConfirmReady: (draft) => draft.rows.length > 0,
  isStepNextDisabled: () => false,
  getAiFilledFields: () => [],
  shouldShowExplainability: () => false,
  buildSuccess: (draft) => {
    const count = draft.rows.length;
    const phoneSetupCount = draft.rows.filter((r) => r.hasMobile).length;
    const cardOrPinCount = draft.rows.filter(
      (r) => (r.hasPin ?? false) || Boolean(r.cardNumber?.trim())
    ).length;
    return {
      result: {
        summaryLine: bulkUserSuccessStatsLine(draft),
        addedCount: count,
        phoneSetupCount,
        cardOrPinCount,
        nextSteps: [
          { id: 'go-to-devices', label: 'Go to Devices' },
          { id: 'add-more-people', label: 'Add more people' },
        ],
      },
      assistantHint: bulkStaticDefaults.assistantHint,
    };
  },
  onApproved: ({ draft, actorEmail }) => {
    const csvText = serializeBulkUserCsv(draft.rows);
    console.log('[bulk-user-import] Final CSV after review edits:\n', csvText);

    if (!actorEmail) return;
    for (const row of draft.rows) {
      registerFromDraft(rowToUserDraft(row), actorEmail);
    }
  },
  buildApproveUserMessage: (draft) => buildBulkApproveUserMessage(draft.rows.length),
};
